export interface TierResolverInput {
  role: "user" | "beta_tester" | "admin" | "practitioner";
  membershipTier: "free" | "paid";
  membershipExpiresAt: Date | string | null;
  tierPreview: "free" | "paid" | null;
}

export function expiryPassed(expiry: Date | string | null): boolean {
  if (!expiry) return false;
  const date = typeof expiry === "string" ? new Date(expiry) : expiry;
  return date.getTime() <= Date.now();
}

/**
 * What tier does this user EFFECTIVELY see right now?
 * Used for feature visibility, UI gating, paywalls.
 * Beta testers can override via tierPreview.
 *
 * NOTE: Intentional duplicate of apps/api/src/lib/tier.ts.
 * Keep in sync manually when either side changes.
 */
export function getEffectiveTier(user: TierResolverInput): "free" | "paid" {
  // Admins and practitioners (deck creators) always have full access — a
  // permanent comp with no expiry. Mirror of apps/api/src/lib/tier.ts.
  if (user.role === "admin" || user.role === "practitioner") return "paid";

  // Beta testers get comped paid access only while the comp is active.
  if (user.role === "beta_tester") {
    const compActive =
      !user.membershipExpiresAt || !expiryPassed(user.membershipExpiresAt);
    if (compActive) return user.tierPreview ?? "paid";
  }

  // Real paying users: check Stripe-driven state
  if (user.membershipTier === "paid" && !expiryPassed(user.membershipExpiresAt)) {
    return "paid";
  }

  return "free";
}

export interface RateLimitInput {
  role: "user" | "beta_tester" | "admin" | "practitioner";
  membershipTier: "free" | "paid";
  membershipExpiresAt: Date | string | null;
}

/**
 * Does this user's request count against a daily cap?
 * Ignores tierPreview — rate-limit decisions are based on TRUE entitlement,
 * not what the user is previewing. Beta testers are never rate-limited,
 * regardless of whether they're previewing free.
 */
export function isRateLimited(user: RateLimitInput): boolean {
  if (user.role === "admin" || user.role === "practitioner") return false;
  if (user.role === "beta_tester") {
    const compActive =
      !user.membershipExpiresAt || !expiryPassed(user.membershipExpiresAt);
    if (compActive) return false;
  }

  if (user.membershipTier === "paid" && !expiryPassed(user.membershipExpiresAt)) {
    return false;
  }

  return true;
}

/**
 * Should the beta-comp UI (founding note, Continue button, Beta toggle) show?
 * True only for a beta tester whose comp is still active and who has NOT yet
 * become a real Stripe subscriber. Web-only (UI concern; not in the api mirror).
 */
export function isBetaCompActive(user: {
  role: "user" | "beta_tester" | "admin" | "practitioner";
  membershipExpiresAt: Date | string | null;
  stripeSubscriptionId?: string | null;
}): boolean {
  if (user.role !== "beta_tester") return false;
  if (user.stripeSubscriptionId) return false;
  return !user.membershipExpiresAt || !expiryPassed(user.membershipExpiresAt);
}
